import { Component, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'

interface Props {
  toolName?: string
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ToolCallErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      '[ToolCallErrorBoundary] Render error in tool:',
      this.props.toolName,
      error,
      info.componentStack,
    )
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm text-destructive">
                Failed to render tool result
              </span>
              {this.props.toolName && (
                <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-destructive/10 text-destructive font-mono">
                  {this.props.toolName}
                </span>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
